import { useEffect, useState } from 'react';

import { fetchNestedMenuItemForTray } from 'src/helper/nestedTrayMenuItem.helper';
import { ItemDetails } from 'src/models/item.model';

interface IParams {
  location_id: string | number | null | undefined;
  category_id: string | null | undefined;
  item_id: number | string | null | undefined;
  user?: { id?: number; type?: string | null };
  /** Fetch only runs while true — lets the caller gate on the picker being open. */
  enabled: boolean;
}

interface IResult {
  data: ItemDetails | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Fetches the full configuration shape for a Berry recommended-item card via
 * the same `getLocationMenuItem` call the rest of the site already uses
 * (reused as-is from src/helper/nestedTrayMenuItem.helper.ts — not
 * reimplemented here). No caching: each picker open gets a fresh fetch, same
 * as src/react-query-hooks/useItem.ts's staleTime/cacheTime: 0.
 */
export default function useBerryMenuItemDetail(params: IParams): IResult {
  const { location_id, category_id, item_id, user, enabled } = params;
  const [data, setData] = useState<ItemDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !location_id || !category_id || !item_id) {
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setData(null);

    fetchNestedMenuItemForTray({
      location_id: String(location_id),
      category_id: String(category_id),
      item_id: String(item_id),
      user,
    })
      .then((result) => {
        if (cancelled) return;
        if (!result) {
          setError('This item is unavailable right now.');
        } else {
          setData(result);
        }
      })
      .catch(() => {
        if (!cancelled) setError('This item is unavailable right now.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, location_id, category_id, item_id]);

  return { data, isLoading, error };
}
